// src/App.js
import React, { useEffect, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProtectedRoute from "./components/ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import PublicRoute from "./components/PublicRoute";
import { Chat } from "stream-chat-react";
import { ClassProvider } from "./context/ClassContext";
import { messaging } from "./firebaseConfig";
import { toast } from "react-toastify";
import { onMessage } from "firebase/messaging";
import TagManager from "react-gtm-module";
import { initAnalytics, trackPageView } from "./utils/analytics";

// Lazy-loaded page components
const Login = React.lazy(() => import("./pages/Login"));
const Learn = React.lazy(() => import("./pages/Learn"));
const Signup = React.lazy(() => import("./pages/Signup"));
const Landing = React.lazy(() => import("./pages/Landing"));
const LanguageGroups = React.lazy(() => import("./pages/LanguageGroups"));
const ClassesUser = React.lazy(() => import("./pages/user/ClassesUser"));
const GroupsUser = React.lazy(() => import("./pages/user/GroupsUser"));
const Unauthorized = React.lazy(() => import("./pages/Unauthorized"));
const GroupDetailUser = React.lazy(() => import("./pages/user/GroupDetailUser"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const LearnLanguageUser = React.lazy(() => import("./pages/user/LearnLanguageUser"));
const AddGroupsUser = React.lazy(() => import("./pages/user/AddGroupsUser"));
const ProfileUser = React.lazy(() => import("./pages/user/ProfileUser"));
const UserEditProfile = React.lazy(() => import("./pages/user/UserEditProfile"));
const UserSettings = React.lazy(() => import("./pages/user/UserSettings"));
const SuperTutorUser = React.lazy(() => import("./pages/user/SuperTutorUser"));
const MessagesUser = React.lazy(() => import("./pages/user/MessagesUser"));
const Splash = React.lazy(() => import("./pages/Splash"));
const LanguageExpertsUser = React.lazy(() => import("./pages/user/LanguageExpertsUser"));
const SavedRecourcesUser = React.lazy(() => import("./pages/user/SavedRecourcesUser"));
const PrivacyPolicyUser = React.lazy(() => import("./pages/user/PrivacyPolicyUser"));
const AboutBambuuUser = React.lazy(() => import("./pages/user/AboutBambuuUser"));
const LoginTutor = React.lazy(() => import("./pages/LoginTutor"));
const ProfileTutor = React.lazy(() => import("./pages/tutor/ProfileTutor"));
const EditProfileTutor = React.lazy(() => import("./pages/tutor/EditProfileTutor"));
const TutorSettings = React.lazy(() => import("./pages/tutor/SettingsTutor"));
const PrivacyPolicyTutor = React.lazy(() => import("./pages/tutor/PrivacyPolicyTutor"));
const AboutBambuuTutor = React.lazy(() => import("./pages/tutor/AboutBambuuTutor"));
const SavedResourcesTutor = React.lazy(() => import("./pages/tutor/SavedResourcesTutor"));
const StudentsTutor = React.lazy(() => import("./pages/tutor/StudentsTutor"));
const InstructorProfileUser = React.lazy(() => import("./pages/user/InstructorProfileUser"));
const BecomeAnExpertUser = React.lazy(() => import("./pages/user/BecomeAnExpertUser"));
const AddClassTutor = React.lazy(() => import("./pages/tutor/AddClassTutor"));
const ClassesTutor = React.lazy(() => import("./pages/tutor/ClassesTutor"));
const GroupsTutor = React.lazy(() => import("./pages/tutor/GroupsTutor"));
const GroupDetailsTutor = React.lazy(() => import("./pages/tutor/GroupDetailsTutor"));
const AddGroupsTutor = React.lazy(() => import("./pages/tutor/AddGroupsTutor"));
const GroupDetailsNotJoinedUser = React.lazy(() => import("./pages/user/GroupDetailsNotJoinedUser"));
const ClassDetailsUser = React.lazy(() => import("./pages/user/ClassDetailsUser"));
const ClassDetailsNotJoinedUser = React.lazy(() => import("./pages/user/ClassDetailsNotJoinedUser"));
const ClassDetailsTutor = React.lazy(() => import("./pages/tutor/ClassDetailsTutor"));
const ExploreClassesUser = React.lazy(() => import("./pages/user/ExploreClassesUser"));
const ExploreGroupsUser = React.lazy(() => import("./pages/user/ExploreGroupsUser"));
const VideoCall = React.lazy(() => import("./pages/user/VideoCall"));
const BammbuuPlusGroupsUser = React.lazy(() => import("./pages/user/BammbuuPlusGroupsUser"));
const SignupSplash = React.lazy(() => import("./pages/SignupSplash"));
const EditGroupsUser = React.lazy(() => import("./pages/user/EditGroupUser"));
const EditGroupsTutor = React.lazy(() => import("./pages/tutor/EditGroupTutor"));
const EditClassPage = React.lazy(() => import("./pages/tutor/EditClassDetails"));
const ProfileSetup = React.lazy(() => import("./pages/user/ProfileSetupUser"));
const DeleteAccout = React.lazy(() => import("./pages/user/DeleteAccout"));
const TutorDeleteAccount = React.lazy(() => import("./pages/tutor/TutorDeleteAccount"));
const VideoCallTutor = React.lazy(() => import("./pages/tutor/VideoCallTutor"));
const LanguagesUser = React.lazy(() => import("./pages/user/LanguagesUser"));
const ContactUs = React.lazy(() => import("./pages/ContactUs"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = React.lazy(() => import("./pages/TermsConditions"));
const Subscriptions = React.lazy(() => import("./pages/Subscriptions"));
const ExamPreparationTutor = React.lazy(() => import("./pages/tutor/ExamPreparationTutor"));
const ExamPreparationUser = React.lazy(() => import("./pages/user/ExamPreparationUser"));
const MobileConfirmationStep = React.lazy(() => import("./components/mobile-flow/MobileConfirmationStep"));
const LandingExamPrep = React.lazy(() => import("./pages/LandingExamPrep"));
const ScholarshipsLanding = React.lazy(() => import("./pages/ScholarshipsLanding"));
const FluencyChallengeLanding = React.lazy(() => import("./pages/FluencyChallengeLanding"));
const FluencyChallengeSuccess = React.lazy(() => import("./pages/FluencyChallengeSuccess"));
const USNursesLanding = React.lazy(() => import("./pages/USNursesLanding"));
const WebSubscriptionStep = React.lazy(() => import("./pages/user/WebSubscriptionStep"));
const ThankYou = React.lazy(() => import("./pages/ThankYou"));
const FreeConsultation = React.lazy(() => import("./pages/FreeConsultation"));
const LandingInterviewXcelerator = React.lazy(() => import("./pages/LandingInterviewXcelerator"));
const LandingInterviewEspanol = React.lazy(() => import("./pages/LandingInterviewEspanol"));
const FreeConsultationInterview = React.lazy(() => import("./pages/FreeConsultationInterview"));

const App = () => {
  const { streamClient, user, setUser } = useAuth(); // Use useAuth() inside the component
  const location = useLocation();

  useEffect(() => {
    // Initialize Google Tag Manager
    const tagManagerArgs = {
      gtmId: process.env.REACT_APP_GTM_ID,
    };
    TagManager.initialize(tagManagerArgs);

    // Initialize our custom analytics
    initAnalytics();
  }, []);

  // Track page views when route changes
  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname);
  }, [location]);

  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      if (payload.notification) {
        const { title, body } = payload.notification;

        toast.info(`${title} - ${body}`);
      }
    });

    // Clean up the listener on unmount
    return () => unsubscribe();
  }, []);

  if (!streamClient) {
    return <div>Loading...</div>;
  }

  return (
    <LanguageProvider user={user} setUser={setUser}>
      <Chat client={streamClient}>
        <ClassProvider>
          <div className="font-urbanist">
            <ToastContainer position="top-right" autoClose={3000} />

            <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
            <Routes>
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <Landing />
                  </PublicRoute>
                }
              />
              <Route
                path="/welcome"
                element={
                  <PublicRoute>
                    <Splash />
                  </PublicRoute>
                }
              />
              <Route
                path="/exam-prep"
                element={
                  <PublicRoute>
                    <LandingExamPrep />
                  </PublicRoute>
                }
              />
              <Route
              path="/interview-accelerator"
              element={
                <PublicRoute>
                  <LandingInterviewXcelerator/>
                </PublicRoute>
              }
              />
              <Route
                path="/es/acelerador-de-entrevistas"
                element={
                  <PublicRoute>
                    <LandingInterviewEspanol />
                  </PublicRoute>
                }
              />
              <Route
                path="/exam-prep/scholarships"
                element={
                  <PublicRoute>
                    <ScholarshipsLanding />
                  </PublicRoute>
                }
              />
              <Route
                path="/fluency-challenge"
                element={
                  <PublicRoute>
                    <FluencyChallengeLanding />
                  </PublicRoute>
                }
              />

              <Route
                path="/exam-prep/nurses"
                element={
                  <PublicRoute>
                    <USNursesLanding />
                  </PublicRoute>
                }
              />

              <Route
                path="/7-day-success"
                element={
                  <PublicRoute>
                    <FluencyChallengeSuccess />                  
                    </PublicRoute>
                }
              />

              <Route
                path="/thank-you"
                element={
                  <PublicRoute>
                    <ThankYou />
                  </PublicRoute>
                }
              />

              <Route
                path="/free-consultation"
                element={
                  <PublicRoute>
                    <FreeConsultation />
                  </PublicRoute>
                }
              />

              <Route
               path="/interview-success"
               element={
                <PublicRoute>
                  <FreeConsultationInterview/>
                </PublicRoute>
               }
              />

              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/terms-and-conditions"
                element={<TermsConditions />}
              />
              <Route path="/unauthorized" element={<Unauthorized />} />

              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />

              <Route
                path="/login-tutor"
                element={
                  <PublicRoute>
                    <LoginTutor />
                  </PublicRoute>
                }
              />
              <Route path="/signup" element={<Signup />} />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/learn"
                element={
                  <ProtectedRoute requiredRole={["student", "tutor"]}>
                    <Learn />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/languageGroups"
                element={
                  <ProtectedRoute requiredRole="student">
                    <LanguageGroups />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/superTutorUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <SuperTutorUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/messagesUser/:channelId?"
                element={
                  <ProtectedRoute requiredRole="student">
                    <MessagesUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/studentsTutor/:channelId?"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <StudentsTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/languageExpertsUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <LanguageExpertsUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addClassTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <AddClassTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/:tutorId"
                element={
                  <ProtectedRoute requiredRole="student">
                    <InstructorProfileUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/becomeAnExpert"
                element={
                  <ProtectedRoute requiredRole="student">
                    <BecomeAnExpertUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/savedRecourcesUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <SavedRecourcesUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute requiredRole={["student", "tutor"]}>
                    <Subscriptions />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/subscribe-exam-prep"
                element={
                  <ProtectedRoute requiredRole="student">
                    <WebSubscriptionStep />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/savedRecourcesTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <SavedResourcesTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/examPreparation"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <ExamPreparationTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profileUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ProfileUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profileTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <ProfileTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/userEditProfile"
                element={
                  <ProtectedRoute requiredRole="student">
                    <UserEditProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutorEditProfile"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <EditProfileTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/userSettings"
                element={
                  <ProtectedRoute requiredRole="student">
                    <UserSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutorSettings"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <TutorSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/privacyPolicyUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <PrivacyPolicyUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/privacyPolicyTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <PrivacyPolicyTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/aboutBambuuUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <AboutBambuuUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aboutBambuuTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <AboutBambuuTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/classesUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ClassesUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/classDetailsUser/:classId"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ClassDetailsUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/classDetailsTutor/:classId"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <ClassDetailsTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/newClassDetailsUser/:classId"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ClassDetailsNotJoinedUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/classesTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <ClassesTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/exploreClassesUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ExploreClassesUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/exploreGroupsUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ExploreGroupsUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/groupsUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <GroupsUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/groupsTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <GroupsTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/groupDetailsUser/:groupId"
                element={
                  <ProtectedRoute requiredRole="student">
                    <GroupDetailUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/newGroupDetailsUser/:groupId"
                element={
                  <ProtectedRoute requiredRole="student">
                    <GroupDetailsNotJoinedUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/groupDetailsTutor/:groupId"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <GroupDetailsTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addGroupsUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <AddGroupsUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addGroupsTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <AddGroupsTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute requiredRole="student">
                    <SignupSplash />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile-setup"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ProfileSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learnLanguageUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <LearnLanguageUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/languages"
                element={
                  <ProtectedRoute requiredRole="student">
                    <LanguagesUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bammbuuPlusGroupsUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <BammbuuPlusGroupsUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/call"
                element={
                  <ProtectedRoute requiredRole="student">
                    <VideoCall />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/callTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <VideoCallTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/editGroup/:groupId"
                element={
                  <ProtectedRoute requiredRole="student">
                    {<EditGroupsUser />}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/editGroupTutor/:groupId"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    {<EditGroupsTutor />}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-class/:classId"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    {<EditClassPage />}
                  </ProtectedRoute>
                }
              />

              <Route
                path="/deleteAccountTutor"
                element={
                  <ProtectedRoute requiredRole={["tutor"]}>
                    <TutorDeleteAccount />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/deleteAccount"
                element={
                  <ProtectedRoute requiredRole={["student"]}>
                    <DeleteAccout />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/examPreparationUser/:tutorId"
                element={
                  <ProtectedRoute requiredRole={["student"]}>
                    <ExamPreparationUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/mobile-confirmation"
                element={<MobileConfirmationStep />}
              />
            </Routes>
            </Suspense>
          </div>
        </ClassProvider>
      </Chat>
    </LanguageProvider>
  );
};

export default App;
